import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Button,
  Form,
  Badge,
  Spinner,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import { getBulkRegisterTaskStatus } from '../data/api';
import messages from './messages';

const POLL_INTERVAL_MS = 3000;

const STATUS_VARIANT = {
  PENDING: 'light',
  IN_PROGRESS: 'primary',
  COMPLETE: 'success',
  FAILED: 'danger',
};

const TaskStatusChecker = () => {
  const intl = useIntl();
  const [taskId, setTaskId] = useState('');
  const [taskStatus, setTaskStatus] = useState(null); // null = not yet fetched
  const [isPolling, setIsPolling] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Clean up on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const fetchStatus = useCallback(async (id) => {
    try {
      const result = await getBulkRegisterTaskStatus(id);
      setTaskStatus(result);
      setFetchError('');

      // Stop polling once terminal state is reached
      if (result.status === 'COMPLETE' || result.status === 'FAILED') {
        stopPolling();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      setFetchError(`Failed to fetch status: ${msg}`);
      stopPolling();
    }
  }, [stopPolling]);

  const handleCheck = useCallback(() => {
    const trimmedId = taskId.trim();
    if (!trimmedId) { return; }

    // Reset previous state
    setTaskStatus(null);
    setFetchError('');
    stopPolling();

    // Immediate first fetch
    fetchStatus(trimmedId);

    // Start polling
    setIsPolling(true);
    intervalRef.current = setInterval(() => {
      fetchStatus(trimmedId);
    }, POLL_INTERVAL_MS);
  }, [taskId, fetchStatus, stopPolling]);

  const handleClear = () => {
    stopPolling();
    setTaskId('');
    setTaskStatus(null);
    setFetchError('');
  };

  const isTerminal = taskStatus?.status === 'COMPLETE' || taskStatus?.status === 'FAILED';
  const percent = taskStatus?.percent ?? 0;
  const processed = taskStatus?.processed ?? 0;
  const total = taskStatus?.total ?? null;
  const errors = taskStatus?.errors ?? [];

  return (
    <div className="task-status-checker mt-3">
      <h6 className="help-sidebar-about-title" style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
        {intl.formatMessage(messages.taskStatusCheckerTitle)}
      </h6>
      <p className="help-sidebar-about-descriptions" style={{ fontSize: '0.78rem', marginBottom: '0.5rem' }}>
        {intl.formatMessage(messages.taskStatusCheckerDescription)}
      </p>

      <Form.Group className="mb-2">
        <Form.Control
          type="text"
          size="sm"
          placeholder={intl.formatMessage(messages.taskStatusCheckerInputPlaceholder)}
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { handleCheck(); } }}
          style={{ fontSize: '0.8rem' }}
        />
      </Form.Group>

      <div className="d-flex" style={{ gap: '0.4rem', marginBottom: '0.5rem' }}>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleCheck}
          disabled={!taskId.trim() || isPolling}
        >
          {isPolling ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              {intl.formatMessage(messages.taskStatusCheckerPolling)}
            </>
          ) : intl.formatMessage(messages.taskStatusCheckerCheckButton)}
        </Button>
        {(taskStatus || fetchError) && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear}>
            {intl.formatMessage(messages.taskStatusCheckerClearButton)}
          </Button>
        )}
        {isPolling && !isTerminal && (
          <Button variant="outline-danger" size="sm" onClick={stopPolling}>
            {intl.formatMessage(messages.taskStatusCheckerStopButton)}
          </Button>
        )}
      </div>

      {fetchError && (
        <p style={{ fontSize: '0.78rem', color: '#c00' }}>{fetchError}</p>
      )}

      {taskStatus && (
        <div>
          {/* Status badge */}
          <div className="mb-2 d-flex align-items-center" style={{ gap: '0.4rem' }}>
            <span style={{ fontSize: '0.78rem' }}>
              {intl.formatMessage(messages.taskStatusCheckerStatusLabel)}
            </span>
            <Badge
              variant={STATUS_VARIANT[taskStatus.status] || 'light'}
              style={{ fontSize: '0.75rem' }}
            >
              {taskStatus.status}
            </Badge>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: '8px',
              borderRadius: '4px',
              background: '#dee2e6',
              overflow: 'hidden',
              marginBottom: '0.3rem',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${percent}%`,
                background: (() => {
                  if (taskStatus.status === 'FAILED') { return '#dc3545'; }
                  if (taskStatus.status === 'COMPLETE') { return '#198754'; }
                  return '#0d6efd';
                })(),
                transition: 'width 0.4s ease',
                borderRadius: '4px',
              }}
            />
          </div>

          {/* Counts */}
          <p style={{ fontSize: '0.78rem', marginBottom: '0.4rem', color: '#555' }}>
            {total !== null
              ? intl.formatMessage(messages.taskStatusCheckerProgress, { processed, total, percent })
              : intl.formatMessage(messages.taskStatusCheckerProgressUnknownTotal, { processed, percent })}
          </p>

          {/* Errors */}
          {errors.length > 0 && (
            <div>
              <p style={{
                fontSize: '0.78rem', fontWeight: 600, color: '#c00', marginBottom: '0.25rem',
              }}
              >
                {intl.formatMessage(messages.taskStatusCheckerErrorsLabel, { count: errors.length })}
              </p>
              <div
                style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  background: '#fff5f5',
                  border: '1px solid #f5c2c7',
                  borderRadius: '4px',
                  padding: '0.4rem 0.5rem',
                }}
              >
                {errors.map((err, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} style={{ fontSize: '0.75rem', marginBottom: '0.2rem', wordBreak: 'break-word' }}>
                    <strong>{err.email}</strong>{' — '}{err.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskStatusChecker;
